import {
  STAFF_CATALOG_BOUNDS,
  type StaffCatalogPlaceCandidate,
  type StaffCatalogPlaceCorrection,
  type StaffCatalogPlaceReasonCode,
  staffCatalogPlaceReasonCodes,
} from "@lafayette/schemas";
import { useState } from "react";

import { CommandDialog, type CommandResult } from "../../../ui/command-dialog";
import { SelectField, TextAreaField } from "../../../ui/fields";
import type { FieldErrors } from "../../validation";
import { reasonOptions } from "../labels";
import { factList, hintText } from "../../components/styles";

// The two dialogs of the place section. Both ask for a code and a reason before they change a match
// (the reason goes into the history of the item). Both put focus on the heading when they open, so a
// screen reader reads the title and the description first.

type Reason = Readonly<{ reasonCode: StaffCatalogPlaceReasonCode | ""; reason: string }>;

/** The code and the reason must both be given. Returns the errors, or the checked correction. */
function checkReason(
  value: Reason,
): { errors: FieldErrors } | { correction: StaffCatalogPlaceCorrection } {
  const errors: Record<string, string> = {};
  const reason = value.reason.trim();
  if (value.reasonCode === "" || !staffCatalogPlaceReasonCodes.includes(value.reasonCode)) {
    errors.reasonCode = "Choose a reason.";
  }
  if (reason === "") errors.reason = "Enter a reason.";
  else if (reason.length > STAFF_CATALOG_BOUNDS.reason) {
    errors.reason = `Use at most ${STAFF_CATALOG_BOUNDS.reason} characters.`;
  }
  if (Object.keys(errors).length > 0 || value.reasonCode === "") return { errors };
  return { correction: { reasonCode: value.reasonCode, reason } };
}

/** What the page's command said: a sentence when it failed (the form stays, the reason is kept), `null` when it worked. */
function reasonResult(failure: string | null): CommandResult<void> {
  return failure === null
    ? { ok: true, value: undefined }
    : { ok: false, failure: { message: failure, retryable: false, keepForm: true } };
}

function ReasonFields({
  idPrefix,
  value,
  errors,
  disabled,
  onChange,
}: Readonly<{
  idPrefix: string;
  value: Reason;
  errors: FieldErrors;
  disabled: boolean;
  onChange: (next: Reason) => void;
}>) {
  return (
    <>
      <SelectField
        id={`${idPrefix}-reason-code`}
        label="Why"
        value={value.reasonCode}
        onChange={(reasonCode) =>
          onChange({ ...value, reasonCode: reasonCode as Reason["reasonCode"] })
        }
        options={reasonOptions}
        placeholder="Choose a reason"
        error={errors.reasonCode}
        required
        disabled={disabled}
      />
      <TextAreaField
        id={`${idPrefix}-reason`}
        label="Reason"
        value={value.reason}
        onChange={(reason) => onChange({ ...value, reason })}
        error={errors.reason}
        required
        rows={3}
        maxLength={STAFF_CATALOG_BOUNDS.reason}
        hint="This goes into the history of the item. Do not paste text from Google."
        disabled={disabled}
      />
    </>
  );
}

type MatchDialogProps = Readonly<{
  candidate: StaffCatalogPlaceCandidate;
  /** The item already has a match: this is a correction, and it needs a reason. */
  replacing: boolean;
  /** Runs P3. Returns a sentence to show when it failed, or `null` when it worked. */
  onConfirm: (correction: StaffCatalogPlaceCorrection | undefined) => Promise<string | null>;
  onClose: () => void;
}>;

/** Confirms the choice of one search result. Only the Place ID and the decision are sent and saved. */
export function MatchDialog({ candidate, replacing, onConfirm, onClose }: MatchDialogProps) {
  const [value, setValue] = useState<Reason>({ reasonCode: "", reason: "" });
  const [errors, setErrors] = useState<FieldErrors>({});
  const correctionOf = (): StaffCatalogPlaceCorrection | undefined => {
    if (!replacing) return undefined;
    const checked = checkReason(value);
    return "correction" in checked ? checked.correction : undefined;
  };
  return (
    <CommandDialog
      title={replacing ? "Correct the match?" : "Match this place?"}
      description={
        replacing
          ? "The item already has a match. This replaces it. The old place id stays in the history."
          : "The item will be matched to the place you chose."
      }
      confirmLabel={replacing ? "Correct the match" : "Match this place"}
      order="confirm-first"
      focusTitle
      focusFailure
      validate={() => {
        if (!replacing) return true;
        const checked = checkReason(value);
        setErrors("errors" in checked ? checked.errors : {});
        return "correction" in checked;
      }}
      mutate={async () => reasonResult(await onConfirm(correctionOf()))}
      onDone={onClose}
      onClose={onClose}
    >
      <dl className={factList}>
        <div>
          <dt>Chosen result</dt>
          <dd className="[overflow-wrap:anywhere]">{candidate.displayName ?? "Unnamed place"}</dd>
        </div>
        <div>
          <dt>Address shown by Google Maps</dt>
          <dd className="[overflow-wrap:anywhere]">
            {candidate.formattedAddress ?? "No address given"}
          </dd>
        </div>
      </dl>
      <p className={hintText}>
        Lafayette saves only the Google place id and your decision. It does not save the name, the
        address, the coordinates or the time zone.
      </p>
      {replacing ? (
        <ReasonFields
          idPrefix="match"
          value={value}
          errors={errors}
          disabled={false}
          onChange={setValue}
        />
      ) : null}
    </CommandDialog>
  );
}

type ClearDialogProps = Readonly<{
  /** `true`: the item has no physical place (state "No place"). `false`: the match is only removed. */
  noPlace: boolean;
  onConfirm: (correction: StaffCatalogPlaceCorrection) => Promise<string | null>;
  onClose: () => void;
}>;

/** Removes the match, or says the item has no physical place. A reason is required for both. */
export function ClearDialog({ noPlace, onConfirm, onClose }: ClearDialogProps) {
  const [value, setValue] = useState<Reason>({ reasonCode: "", reason: "" });
  const [errors, setErrors] = useState<FieldErrors>({});
  const label = noPlace ? "Mark as no physical place" : "Remove the match";
  return (
    <CommandDialog
      title={noPlace ? "Mark this item as having no physical place?" : "Remove the match?"}
      description={
        noPlace
          ? "Use this for an item that is not at one place, such as a service. Any place id is removed. The history keeps it."
          : "The item will have no place id. The history keeps the old one."
      }
      confirmLabel={label}
      tone="danger"
      order="confirm-first"
      focusTitle
      focusFailure
      validate={() => {
        const checked = checkReason(value);
        setErrors("errors" in checked ? checked.errors : {});
        return "correction" in checked;
      }}
      mutate={async () => {
        const checked = checkReason(value);
        if ("errors" in checked) return reasonResult("Check the reason.");
        return reasonResult(await onConfirm(checked.correction));
      }}
      onDone={onClose}
      onClose={onClose}
    >
      <ReasonFields
        idPrefix="clear"
        value={value}
        errors={errors}
        disabled={false}
        onChange={setValue}
      />
    </CommandDialog>
  );
}
